import React, { useState } from 'react';

import { Box } from '@wds/box';
import { Typography } from '@wds/typography';
import { Menu } from '@wds/menu';
import { IconCaretDown } from '@wds/icons';
import { Link } from '@wds/link';

import PropTypes from 'prop-types';
import STRINGS from '../../../../constants';
import Search from '../Search';
import getComponentWithTheme from '../../../../../themeProvider';
import { hasAnyPermission, hasPermission } from '../../../../utils';
import LanguageSelector from '../LanguageSelector';
import he from 'he';

const AdminNavigation = (props) => {
	const {
		userDetails, languages, assumedRole, siteInfo, permissions, language, setLanguage, cdnUrl,
	} = props;
	const [showSearch, setShowSearch] = useState(false);

	const navBarItemProps = {
		mx: 4,
		style: {
			color: '#efefef',
			cursor: 'pointer',
			fontSize: '1.5rem',
		},
	};

	const navBarMenuProps = {
		style: navBarItemProps.style,
		display: 'flex',
		justifyContent: 'space-around',
		alignItems: 'center',
		gap: 1,
	};

	const settingsMenuItems = [
		{
			icon: <Typography color='darkMuted'><i className='nc-icon-mini ui-1_settings-gear-64' /></Typography>,
			label: STRINGS.navMenu.admin.general,
			href: reverse('admin-settings-general'),
			show: hasPermission(permissions.permissions, permissions.PermissionTypes.SettingsGeneral),
		},
		{
			icon: <Typography color='darkMuted'><i className='nc-icon-mini files_single-paragraph' /></Typography>,
			label: STRINGS.navMenu.admin.pages,
			href: reverse('admin-settings-pages'),
			show: hasPermission(permissions.permissions, permissions.PermissionTypes.SettingsPages),
		},
		{
			icon: <Typography color='darkMuted'><i className='nc-icon-mini envir_recycling' /></Typography>,
			label: STRINGS.navMenu.admin.reusableApplicantTasks,
			href: reverse('admin-settings-global-tasks'),
			permissionRequired: 'SettingsGlobalTasks',
			show: hasPermission(permissions.permissions, permissions.PermissionTypes.SettingsGlobalTasks),
		},
		{
			icon: <Typography color='darkMuted'><i className='nc-icon-mini shopping_basket' /></Typography>,
			label: STRINGS.navMenu.admin.files,
			href: reverse('admin-settings-files'),
			show: hasPermission(permissions.permissions, permissions.PermissionTypes.SettingsFiles),
		},
		{
			icon: <Typography color='darkMuted'><i className='nc-icon-mini users_add-27' /></Typography>,
			label: STRINGS.navMenu.admin.eligibility,
			href: reverse('admin-awards-eligibility-quiz'),
			show: hasPermission(permissions.permissions, permissions.PermissionTypes.SettingsEligibility),
		},
		{
			icon: <Typography color='darkMuted'><i className='nc-icon-mini shopping_tag' /></Typography>,
			label: STRINGS.navMenu.admin.customFields,
			href: reverse('admin-settings-customfields'),
			show: hasPermission(permissions.permissions, permissions.PermissionTypes.SettingsCF),
		},
		{
			icon: <Typography color='darkMuted'><i className='nc-icon-mini users_badge-13' /></Typography>,
			label: STRINGS.navMenu.admin.registration,
			href: reverse('admin-settings-registration'),
			show: hasPermission(permissions.permissions, permissions.PermissionTypes.SettingsRegistration),
		},
		{
			icon: <Typography color='darkMuted'><i className='nc-icon-mini shopping_basket' /></Typography>,
			label: STRINGS.navMenu.admin.email,
			href: reverse('admin-settings-emails'),
			show: hasPermission(permissions.permissions, permissions.PermissionTypes.SettingsEmail),
		},
		{
			icon: <Typography color='darkMuted'><i className='nc-icon-mini users_circle-08' /></Typography>,
			label: STRINGS.navMenu.admin.administrators,
			href: reverse('admin-permissions'),
			show: hasPermission(permissions.permissions, 'full'),
		},
		{
			icon: <Typography color='darkMuted'><i className='nc-icon-mini design_app' /></Typography>,
			label: STRINGS.navMenu.admin.integrations,
			href: reverse('admin-settings-integrations'),
			show: hasPermission(permissions.permissions, permissions.PermissionTypes.SettingsIntegrations),
		},
		{
			icon: <Typography color='darkMuted'><i className='nc-icon-mini business_globe' /></Typography>,
			label: STRINGS.navMenu.admin.translations,
			href: reverse('admin-settings-translations-index'),
			show: hasPermission(permissions.permissions, permissions.PermissionTypes.SettingsTranslation),
		},
		{
			icon: <Typography color='darkMuted'><i className='nc-icon-mini objects_spaceship' /></Typography>,
			label: STRINGS.navMenu.admin.superuser,
			href: reverse('admin-settings-superuser'),
			show: userDetails.isSuperuser,
		},
	];

	const userMenuRoleChoices = [
		{
			label: STRINGS.navMenu.common.applicant,
			href: reverse('application-dashboard'),
			show: userDetails?.roles?.includes('applicant'),
			id: 'sma-applicant-role',
		},
		{
			label: STRINGS.navMenu.common.reviewer,
			href: reverse('reviewers-index'),
			show: userDetails?.roles?.includes('reviewer'),
			id: 'sma-reviewer-role',
		},
		{
			label: STRINGS.navMenu.common.recommender,
			href: reverse('recommenders-task-list'),
			show: userDetails?.roles?.includes('recommender'),
			id: 'sma-recommender-role',
		},
		{
			label: STRINGS.navMenu.common.administrator,
			href: reverse('admin-dashboard'),
			show: userDetails?.roles?.includes('admin'),
			id: 'sma-admin-role',
		},
	];

	const userMenuCommonItems = [
		{ label: STRINGS.navMenu.common.myAccount, href: reverse('account') },
		{ label: STRINGS.navMenu.common.signOut, href: reverse('signout') },
	];

	const helpMenuChoices = [
		{
			icon: null,
			label: STRINGS.navMenu.admin.contactSupport,
			href: reverse('helpdesk'),
			show: true,
		},
		{
			icon: null,
			label: STRINGS.navMenu.common.helpCenter,
			href: reverse('help-center'),
			show: true,
		},
		{
			icon: null,
			label: STRINGS.navMenu.admin.featureFeedback,
			href: reverse('feature-request'),
			show: true,
		},
	];

	const userMenuItems = () => (
		<>
			{userMenuRoleChoices.some((item) => item.show) && (
				<Menu.Group title={STRINGS.navMenu.common.switchRoleTo} aria-label={STRINGS.labels.roleChoices}>
					{userMenuRoleChoices.map((item) => {
						if (item.show) {
							return (
								<Menu.Item
									id={item.id}
									key={item.href}
									onClick={() => {
										window.location.href = item.href;
									}}
									aria-label={item.label}
								>
									{item.label}
								</Menu.Item>
							);
						}
						return null;
					})}
				</Menu.Group>
			)}
			{assumedRole && (
				<Menu.Group aria-label={STRINGS.labels.assumedRoleControls}>
					<Menu.Item
						id='sma-assumed-role-return'
						key='assumed-role-return'
						onClick={() => {
							window.location.href = assumedRole.return_url;
						}}
					>
						{STRINGS.returnToOriginalRole}
					</Menu.Item>
				</Menu.Group>
			)}
			<Menu.Group aria-label={STRINGS.navMenu.common.userMenu}>
				{userMenuCommonItems.map((item) => (
					<Menu.Item
						key={item.href}
						onClick={() => {
							window.location.href = item.href;
						}}
						aria-label={item.label}
					>
						{item.label}
					</Menu.Item>
				))}
			</Menu.Group>
		</>
	);

	const navMenuItems = [
		{
			id: 'site-dashboard',
			title: STRINGS.labels.siteDashboard,
			icon: 'nc-icon-mini business_award-48',
			href: reverse('admin-dashboard'),
			showTitle: false,
			show: true,
		},
		{
			id: 'financial-management',
			title: STRINGS.labels.financialManagement,
			icon: 'nc-icon-mini business_bank',
			href: reverse('admin-financial-management-program-budgets'),
			showTitle: false,
			show: siteInfo.hasFinancialManagement
				&& hasAnyPermission(permissions.permissions, permissions.PermissionTypesByGroup.FinancialManagement),
		},
		{
			id: 'reports',
			title: STRINGS.labels.reports,
			icon: 'nc-icon-mini health_pulse-chart',
			href: reverse('admin-reports'),
			showTitle: false,
			show: hasPermission(permissions.permissions, permissions.PermissionTypes.Reports),
		},
		{
			id: 'users',
			title: STRINGS.labels.users,
			icon: 'nc-icon-mini users_multiple-11',
			href: reverse('admin-users'),
			showTitle: false,
			show: hasAnyPermission(permissions.permissions, permissions.PermissionTypesByGroup.User),
		},
		{
			id: 'activity',
			title: STRINGS.labels.activity,
			icon: 'nc-icon-mini health_pulse',
			href: reverse('admin-activity'),
			showTitle: false,
			show: true,
		},
		{
			id: 'settings',
			title: STRINGS.labels.settings,
			icon: 'nc-icon-mini loader_gear',
			menuItems: settingsMenuItems,
			showTitle: false,
			show: hasAnyPermission(permissions.permissions, permissions.PermissionTypesByGroup.Settings),
		},
		{
			id: 'help',
			title: STRINGS.labels.help,
			icon: 'nc-icon-mini ui-2_alert-circle-i',
			menuItems: helpMenuChoices,
			showTitle: false,
			show: true,
		},
	];

	return (
		getComponentWithTheme(
			<Box
				role='region'
				tabIndex='0'
				display='flex'
				justifyContent='space-between'
				alignItems='center'
				style={{
					padding: '0 1rem',
					height: '48px',
					backgroundColor: '#05467e',
					width: '100%',
					position: 'fixed',
					zIndex: 1033,
				}}
			>
				<Box
					tabIndex='0'
					aria-label='Site Title'
					element='a'
					display='flex'
					justifyContent='flex-start'
					href='/'
					alignItems='center'
					gap={4}
				>
					<img src={`${cdnUrl}img/smapply/monkey_head.svg`} height='36' alt='' />
					<Typography color='light' weight='medium' variant='body'>{he.decode(siteInfo.title)}</Typography>
				</Box>
				<Box
					display='flex'
					justifyContent='space-between'
					alignItems='center'
				>
					{siteInfo.localizationEnabled && (
						<Box {...navBarMenuProps} mr={2} aria-label={STRINGS.labels.languageSelector} role='button'>
							<LanguageSelector
								languages={languages}
								currentLanguage={language}
								setCurrentLanguage={setLanguage}
								type='menu'
							/>
						</Box>
					)}
					<Box
						element='button'
						style={{ background: 'none', color: '#ffffff', border: 'none' }}
						mr={2}
						tabIndex='0'
						onClick={() => setShowSearch(!showSearch)}
						role='button'
						aria-label='languages'
					>
						<div>
							<Box
								display='flex'
								justifyContent='space-around'
								alignItems='center'
								gap={0}
							>
								<Box
									{...navBarItemProps}
									display='flex'
									justifyContent='center'
									alignContent='center'
									gap={4}
								>
									<Typography lineHeight={1.5} style={{ fontSize: '24px', color: '#ffffff' }}>
										<i className='nc-icon-mini ui-1_zoom' aria-label={STRINGS.labels.searchIcon} />
									</Typography>
									<Typography lineHeight={2}>
										<IconCaretDown
											color='light'
											size='sm'
											aria-label={STRINGS.labels.chevronDown}
										/>
									</Typography>
								</Box>
							</Box>
						</div>
					</Box>
					{
						navMenuItems.map((item) => {
							if (item.show) {
								if (item.menuItems) {
									return (
										<Box
											{...navBarMenuProps}
											key={`navItem-${item.id}`}
											aria-label={item.title}
											role='button'
										>
											<Menu
												menuTrigger={(
													<div>
														<Box
															display='flex'
															justifyContent='space-around'
															alignItems='center'
															gap={0}
														>
															<Box {...navBarItemProps}>
																<i className={item.icon} />
																{item.showTitle && (
																	<Typography
																		component='span'
																		color='light'
																		weight='medium'
																		variant='body'
																	>
																		{item.title}
																	</Typography>
																)}
															</Box>
															<IconCaretDown
																color='light'
																size='sm'
																aria-label={STRINGS.labels.chevronDown}
															/>
														</Box>
													</div>
												)}
												noRules
												noMaxHeight
												placement='bottom-center'
											>
												{item.menuItems && item.menuItems.map((subItem) => (
													subItem.show ? (
														<Menu.Item
															key={subItem.href}
															addOn={subItem.icon}
															onClick={() => {
																if (subItem.external) {
																	window.open(subItem.href, '_blank');
																} else {
																	window.location.href = subItem.href;
																}
															}}
														>
															{subItem.label}
														</Menu.Item>
													) : null
												))}
											</Menu>
										</Box>
									);
								}
								return (
									<Link key={item.href} href={item.href} aria-label={item.title}>
										<Box {...navBarItemProps} key={item.href}>
											<i className={item.icon} />
											{item.showTitle && (
												<Typography
													component='span'
													color='light'
													weight='medium'
													variant='body'
												>
													{item.title}
												</Typography>
											)}
										</Box>
									</Link>
								);
							}
							return null;
						})
					}
					<Box {...navBarMenuProps} ml={4} aria-label={STRINGS.labels.userMenu} role='button'>
						<Menu
							menuTrigger={(
								<div>
									<Box display='flex' justifyContent='space-around' alignItems='center' gap={2}>
										<Typography
											component='span'
											color='light'
											weight='medium'
											variant='body'
										>
											{userDetails.firstName}
											{' '}
											{userDetails.lastName}
										</Typography>
										<IconCaretDown
											color='light'
											size='sm'
											aria-label={STRINGS.labels.chevronDown}
										/>
									</Box>
								</div>
							)}
							noRules
							noMaxHeight
							placement='bottom-center'
						>
							{userMenuItems()}
						</Menu>
					</Box>
				</Box>
				<Search show={showSearch} setShow={setShowSearch} />
			</Box>,
		)
	);
};

AdminNavigation.propTypes = {
	userDetails: PropTypes.object.isRequired,
	languages: PropTypes.object.isRequired,
	assumedRole: PropTypes.object,
	siteInfo: PropTypes.object.isRequired,
	pages: PropTypes.array,
	language: PropTypes.string.isRequired,
	setLanguage: PropTypes.func.isRequired,
	cdnUrl: PropTypes.string.isRequired,
};
AdminNavigation.defaultProps = {
	assumedRole: {},
	pages: [],
};

export default AdminNavigation;
